import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { promptTemplateApi } from "@/apis/promptTemplate";
import Router from "@/constants/router";
import { PromptTemplateDto, PromptTemplateListRequestDto } from "@/dto/PromptTemplateDto";
import { ResultListDto } from "@/dto/ResponseDto";

export default function usePromptTemplateList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<ResultListDto<PromptTemplateDto> | null>(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentLimit, setCurrentLimit] = useState<number>(20);

  const fetchData = useCallback(async (params: PromptTemplateListRequestDto = {}) => {
    setLoading(true);
    try {
      const page = params.page || currentPage;
      const limit = params.limit || currentLimit;
      const result = await promptTemplateApi.getList({
        page,
        limit,
        category: params.category !== undefined ? params.category : selectedCategory || undefined,
        search: params.search !== undefined ? params.search : searchKeyword || undefined,
      });
      setData(result);
      setCurrentPage(page);
      setCurrentLimit(limit);
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to fetch prompt templates", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentLimit, selectedCategory, searchKeyword, enqueueSnackbar]);

  const fetchCategories = useCallback(async () => {
    try {
      const cats = await promptTemplateApi.getCategories();
      setCategories(cats);
    } catch (error: any) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, [fetchData, fetchCategories]);

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    fetchData({ category: category || undefined, page: 1 });
  }, [fetchData]);

  const handleSearch = useCallback((keyword: string) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
    fetchData({ search: keyword || undefined, page: 1 });
  }, [fetchData]);

  const handlePageChange = useCallback(
    (_: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
      setCurrentPage(page + 1);
      fetchData({ page: page + 1 });
    },
    [fetchData]
  );

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newLimit = parseInt(event.target.value, 10);
      setCurrentLimit(newLimit);
      setCurrentPage(1);
      fetchData({ limit: newLimit, page: 1 });
    },
    [fetchData]
  );

  const handleCreate = useCallback(() => {
    navigate(Router.PromptTemplateCreate);
  }, [navigate]);

  const handleDetail = useCallback((templateId: number) => {
    navigate(Router.PromptTemplateDetail.replace(":id", templateId.toString()));
  }, [navigate]);

  const handleDelete = useCallback(async (templateId: number) => {
    if (!confirm("Are you sure you want to delete this prompt template?")) return;
    try {
      await promptTemplateApi.delete(templateId);
      enqueueSnackbar("Prompt template deleted successfully.", { variant: "success" });
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to delete prompt template", { variant: "error" });
    }
  }, [fetchData, enqueueSnackbar]);

  return {
    data,
    loading,
    categories,
    selectedCategory,
    searchKeyword,
    handleCategoryChange,
    handleSearch,
    handleCreate,
    handleDetail,
    handleDelete,
    handlePageChange,
    handleRowsPerPageChange,
  };
}